import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { settingsAPI, portfolioAPI } from '../utils/api';
import { updatePageMeta, addStructuredData } from '../utils/seo';
import { metaFor, homePageSchema } from '../seo/routeMeta';
import { sizedImage, fallbackToOriginal } from '../utils/cloudinary';
import { motion } from 'framer-motion';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');

/**
 * Size a hero image at the CDN.
 *
 * The carousel was rendering raw originals — often 4000px+ — which the browser
 * downscaled to the viewport and then CSS scaled back up 4% for the drift
 * effect. Browser downscaling of a very large bitmap is soft to begin with, and
 * the upscale compounds it, which is why the hero looked noticeably worse than
 * the same photo in a gallery.
 *
 * Requesting a sensible width with `q_auto:good,f_auto` gets a sharp, properly
 * resampled image at a fraction of the bytes. `c_limit` never enlarges, so a
 * smaller original is left alone rather than blown up.
 */
function heroSrc(url, w) {
  if (!url || !url.includes('/upload/')) return url;
  return url.replace('/upload/', `/upload/c_limit,w_${w},q_auto:good,f_auto/`);
}

function Home() {
  const [settings, setSettings] = useState(null);
  const [featuredItems, setFeaturedItems] = useState([]);
  // Largest credit-pack bonus, read live from /api/credit-packs so the band
  // never states a number the packs don't honour. null = say it without one.
  const [maxCreditBonus, setMaxCreditBonus] = useState(null);
  // Hero showcase rotation
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Set SEO metadata
    updatePageMeta(metaFor('/'));
    
    // Same WebPage block the server prints into the raw HTML.
    addStructuredData(homePageSchema());
    
    fetchSettings();
    fetchFeaturedPortfolio();
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/credit-packs`)
      .then(r => (r.ok ? r.json() : null))
      .then(j => {
        const pcts = Array.isArray(j?.data) ? j.data.map(p => Number(p.bonusPercent) || 0) : [];
        if (pcts.length && Math.max(...pcts) > 0) setMaxCreditBonus(Math.max(...pcts));
      })
      .catch(() => { /* band still renders, without a figure */ });
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      // The public settings endpoint wraps its payload in { success, data }.
      // Unwrap it so fields can be read directly — the previous code read
      // settings.banner off the envelope, which was always undefined.
      setSettings(response.data?.data || response.data || null);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchFeaturedPortfolio = async () => {
    try {
      const response = await portfolioAPI.getAll({ featured: true });
      let items = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      // SOFT DEFAULT: if nothing is flagged "featured" yet, fall back to the
      // rest of the portfolio. A homepage showing no photography is far worse
      // than one showing un-curated photography.
      if (!items.length) {
        const all = await portfolioAPI.getAll();
        items = Array.isArray(all.data) ? all.data : (all.data?.data || []);
      }
      setFeaturedItems(items.slice(0, 8));
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    }
  };

  // Hero showcase: still images only. Embedded videos have no usable file URL
  // and direct video makes a heavy, jittery full-bleed background.
  const heroImages = React.useMemo(() => {
    const out = [];
    for (const item of featuredItems) {
      const media = item.images?.[0];
      if (!media || media.embedUrl) continue;
      const raw = media.url || '';
      if (!raw) continue;
      const isVideo = media.mediaType === 'video' || raw.includes('/video/') ||
        /\.(mp4|mov|webm)$/i.test(raw);
      if (isVideo) continue;
      const abs = raw.startsWith('http') ? raw : `${API_BASE}${raw}`;
      out.push({
        url: heroSrc(abs, 2000),
        // The drift scales to 1.04, so the 2560 step covers a 2460px viewport
        // without the browser ever having to enlarge the bitmap.
        srcSet: [960, 1440, 2000, 2560].map(w => `${heroSrc(abs, w)} ${w}w`).join(', '),
        title: item.title || 'Utah real estate photography'
      });
    }
    return out.slice(0, 6);
  }, [featuredItems]);

  const activeHero = heroImages.length ? heroIndex % heroImages.length : 0;

  // Advance the showcase. Skipped entirely when the visitor prefers reduced
  // motion — they get a single still rather than a rotation.
  useEffect(() => {
    if (heroImages.length < 2) return undefined;
    const reduced = typeof window !== 'undefined' && window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;
    const id = setInterval(() => {
      setHeroIndex(i => (i + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(id);
  }, [heroImages.length]);

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero" itemScope itemType="https://schema.org/WPHeader">
        <div className="hero-stage">
          {heroImages.map((img, i) => (
            <img
              key={img.url}
              src={img.url}
              srcSet={img.srcSet}
              sizes="100vw"
              alt={i === 0 ? 'Utah real estate photography by Utah Realty Photos' : ''}
              className={`hero-slide${i === activeHero ? ' is-active' : ''}`}
              loading={i === 0 ? 'eager' : 'lazy'}
              {...(i === 0 ? { itemProp: 'image' } : {})}
            />
          ))}
        </div>
        <div className="hero-scrim"></div>
        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            itemProp="headline"
          >
            Professional Real Estate Photography in Utah
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            itemProp="description"
          >
            Professional photography for realtors and home sellers. Drone shots, twilight photography, and cinematic video tours with fast turnaround.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem' }}
          >
            {/* Booking is the conversion; the portfolio is proof. The primary
                button used to be "View Portfolio". (Fable review F18) */}
            <Link to="/book" className="btn btn-primary" aria-label="Book a real estate photography session">
              Book a Session
            </Link>
            <Link to="/pricing" className="btn btn-secondary" aria-label="See real estate photography pricing">
              See Pricing
            </Link>
          </motion.div>
        </div>
        {heroImages.length > 1 && (
          <div className="hero-rail" role="tablist" aria-label="Featured work">
            {heroImages.map((img, i) => (
              <button
                key={img.url}
                type="button"
                role="tab"
                aria-selected={i === activeHero}
                aria-label={`Show ${img.title}`}
                className={`hero-tick${i === activeHero ? ' is-active' : ''}`}
                onClick={() => setHeroIndex(i)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Featured Portfolio Carousel — right after hero */}
      {featuredItems.length > 0 && (
        <section className="featured-carousel-section" itemScope itemType="https://schema.org/ImageGallery">
          <div className="featured-carousel-header">
            <h2 itemProp="name">Featured Work</h2>
            <Link to="/portfolio" className="carousel-view-all">View Full Portfolio →</Link>
          </div>
          <div className="featured-carousel-track">
            <div className="featured-carousel-inner">
              {/* Duplicate items for seamless loop */}
              {[...featuredItems, ...featuredItems].map((item, index) => (
                <div key={`${item._id}-${index}`} className="featured-carousel-slide" itemScope itemType="https://schema.org/ImageObject">
                  {item.images?.[0] && (() => {
                    const media = item.images[0];
                    const url = media.url?.startsWith('http') ? media.url : `${API_BASE}${media.url || ''}`;
                    // Embedded video (YouTube/Vimeo) — render poster image, NOT <video>.
                    // <video src=youtube-url> throws NotSupportedError and shows a blank tile.
                    if (media.embedUrl) {
                      const ytMatch = media.embedUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
                      const posterUrl = ytMatch ? `https://i.ytimg.com/vi/${ytMatch[1]}/hqdefault.jpg` : null;
                      if (posterUrl) {
                        return <img src={posterUrl} alt={`${item.title} - Utah real estate photography`} itemProp="contentUrl" loading="lazy" />;
                      }
                      // Vimeo or unknown embed: render a static dark tile rather than a broken element
                      return (
                        <div style={{ width: '100%', height: '100%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', opacity: 0.6, fontSize: '14px' }}>
                          Video
                        </div>
                      );
                    }
                    // Direct video file — safe to use <video>
                    const isDirectVideo = media.mediaType === 'video' || url.includes('/video/') || url.endsWith('.mp4') || url.endsWith('.mov') || url.endsWith('.webm');
                    return isDirectVideo ? (
                      <video src={url} muted playsInline loop autoPlay itemProp="contentUrl" />
                    ) : (
                      <img src={sizedImage(url, 900)} alt={`${item.title} - Utah real estate photography`} itemProp="contentUrl" loading="lazy" decoding="async" onError={fallbackToOriginal(url)} />
                    );
                  })()}
                  <div className="featured-carousel-caption">
                    <span className="carousel-category">{item.category}</span>
                    <h3 itemProp="name">{item.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Section */}
      <section className="section" style={{ background: 'white' }} itemScope itemType="https://schema.org/ItemList">
        <div className="container">
          <div className="section-title">
            <h2 itemProp="name">Real Estate Photography Services</h2>
            <p itemProp="description">Comprehensive photography solutions for Utah realtors, home sellers, and property managers</p>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem',
            marginTop: '3rem'
          }}>
            {[
              {
                title: 'Residential Photography',
                description: 'Stunning interior and exterior shots that showcase homes at their best. Perfect for single-family homes, condos, and townhouses.',
                link: '/services/residential'
              },
              {
                title: 'Commercial Properties',
                description: 'Professional photography for offices, retail spaces, and commercial buildings. Ideal for leasing and sales.',
                link: '/services/commercial'
              },
              {
                title: 'Aerial & Drone Photography',
                description: 'Breathtaking aerial drone shots that provide unique perspectives. Perfect for luxury homes and large properties.',
                link: '/services/drone'
              },
              {
                title: 'Twilight Photography',
                description: 'Magical golden hour shots that make properties stand out in MLS listings and marketing materials.',
                link: '/services/twilight'
              },
              {
                title: 'Video Tours',
                description: 'Engaging cinematic video walkthroughs with drone footage that bring properties to life for online buyers.',
                link: '/services/video'
              },
              {
                title: 'Virtual Tours',
                description: 'Interactive 3D Matterport tours that let potential buyers explore properties from anywhere in the world.',
                link: '/services/virtual-tours'
              },
              {
                // Land (2026-10-12): full width, below the other six cards.
                title: 'Land & Lot Photography',
                description: 'Aerial photos covering the whole parcel with the boundary drawn on, plus ground photos of the access, terrain and views. $195 for the first acre, $100 for each additional acre — any size books online.',
                link: '/services/land',
                wide: true
              }
            ].map((service, index) => (
              <motion.article
                key={index}
                className="card"
                style={service.wide ? { gridColumn: '1 / -1' } : undefined}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                itemScope
                itemType="https://schema.org/Service"
                itemProp="itemListElement"
              >
                <h3 itemProp="name">{service.title}</h3>
                <p itemProp="description">{service.description}</p>
                <Link to={service.link} className="service-link" itemProp="url" aria-label={`Learn more about ${service.title}`}>
                  Learn More →
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section" style={{ background: 'var(--light)' }}>
        <div className="container">
          <div className="section-title">
            <h2>Serving Utah County &amp; the Wasatch Front</h2>
            <p>Professional real estate photography across Utah County and the Wasatch Front, with travel beyond</p>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem',
            textAlign: 'center',
            marginTop: '2rem'
          }}>
            {[
              'Provo', 'Orem', 'Lehi', 'American Fork',
              'Pleasant Grove', 'Spanish Fork', 'Springville', 'Payson',
              'Saratoga Springs', 'Eagle Mountain'
            ].map((city, index) => (
              <div key={index} style={{ padding: '1rem' }}>
                <strong>{city}</strong>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Link to="/locations" className="btn btn-outline">View All Service Areas</Link>
          </p>
        </div>
      </section>

      {/* Prepaid credit. Replaced the monthly-subscription pitch, which sold a
          product retired from public view (code intact — HANDOFF). */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #fef9c3 0%, #fef3c7 100%)', borderTop: '1px solid #fde68a', borderBottom: '1px solid #fde68a' }}>
        <div className="container" style={{ textAlign: 'center', maxWidth: '720px' }}>
          <h2 style={{ color: '#78350f', marginBottom: '0.75rem' }}>List often? Prepay and get more</h2>
          <p style={{ color: '#92400e', fontSize: '1.05rem', marginBottom: '1.5rem' }}>
            {maxCreditBonus
              ? <>Prepaid credit packs add <strong>up to {maxCreditBonus}% bonus credit</strong> on top of what you put in. No subscription, and credit never expires.</>
              : <>Prepaid credit packs add <strong>bonus credit</strong> on top of what you put in. No subscription, and credit never expires.</>}
          </p>
          <Link to="/credit" className="btn btn-secondary" style={{ background: '#d97706', borderColor: '#d97706', color: 'white' }}>
            See Credit Packs
          </Link>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section" style={{ background: 'var(--primary)', color: 'white' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ color: 'white', marginBottom: '1rem' }}>Ready to Make Your Listing Stand Out?</h2>
          <p style={{ fontSize: '1.2rem', marginBottom: '2rem', color: 'rgba(255,255,255,0.9)' }}>
            Professional real estate photography that helps properties sell faster and for more money
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/book" className="btn btn-primary">
              Schedule Your Photo Shoot
            </Link>
            <Link to="/pricing" className="btn btn-secondary">
              View Pricing Packages
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="section">
        <div className="container" style={{ textAlign: 'center' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '3rem',
            marginTop: '2rem'
          }}>
            <div>
              <h3 style={{ fontSize: '3rem', color: 'var(--secondary)', marginBottom: '0.5rem' }}>4+</h3>
              <p>Service Packages</p>
            </div>
            <div>
              <h3 style={{ fontSize: '3rem', color: 'var(--secondary)', marginBottom: '0.5rem' }}>Fast</h3>
              <p>Turnaround Time</p>
            </div>
            <div>
              <h3 style={{ fontSize: '3rem', color: 'var(--secondary)', marginBottom: '0.5rem' }}>HDR</h3>
              <p>Professional Quality</p>
            </div>
            <div>
              <h3 style={{ fontSize: '3rem', color: 'var(--secondary)', marginBottom: '0.5rem' }}>Local</h3>
              <p>Utah County &amp; the Wasatch Front</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
